import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData
} from '../common/resumeHelpers';
import { 
  SectionTitleWithLine,
  ContactInfo,
  ResumeSection,
  SummarySection,
  CoreCompetenciesSection,
  ExperienceSection,
  EducationSection,
  CertificateSection,
  PaperSection,
  SkillSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 5 - 모던한 2열 구조
const Template5 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  // 이름을 성과 이름으로 분리
  const nameParts = currentData.name.split(' ');
  const firstName = nameParts.slice(0, -1).join(' ') || nameParts[0] || currentData.name;
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

  return (
    <div className={`template-5 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      {/* Header Section */}
      <div className="flex border-b border-gray-300">
        {/* Left Header */}
        <div className="w-2/3 p-8 pr-6">
          {/* Name and Title */}
          <div>
            <h1 className={`text-5xl font-light text-gray-800 mb-1 uppercase ${!hasRealData.name ? 'italic text-gray-300' : ''}`}>
              {firstName}
            </h1>
            {lastName && (
              <h1 className={`text-5xl font-light text-gray-800 mb-4 uppercase ${!hasRealData.name ? 'italic text-gray-300' : ''}`}>
                {lastName}
              </h1>
            )}
            <p className={!hasRealData.title ? 'text-gray-300 italic text-sm uppercase title-spacing' : 'text-sm text-gray-600 uppercase title-spacing'}>
              {currentData.title}
            </p>
          </div>
        </div>

        {/* Vertical Divider */}
        <div className="w-px bg-gray-300"></div>

        {/* Right Header */}
        <div className="w-1/3 p-8 pl-6">
          {/* Contact Info */}
          <ContactInfo
            phone={currentData.phone}
            email={currentData.email}
            socialLinks={currentData.socialLinks}
            hasRealData={hasRealData}
            direction="vertical"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex">
        {/* Left Column */}
        <div className="w-2/3 p-8 pr-6">

          {/* Profile */}
          {currentData.summary && (
            <ResumeSection 
              variant="underlined"
              title={titles.profile}
              sectionClassName="mb-10"
            >
              <SummarySection 
                summary={currentData.summary}
                hasRealData={hasRealData}
                textClassName="text-sm text-gray-600"
              />
            </ResumeSection>
          )}

          {/* Core Competencies */}
          {currentData.keyStrengths && currentData.keyStrengths.length > 0 && (
            <ResumeSection 
              variant="underlined"
              title={titles.coreCompetencies}
              sectionClassName="mb-10"
            >
              <CoreCompetenciesSection 
                keyStrengths={currentData.keyStrengths}
                hasRealData={hasRealData}
                itemClassName="text-sm"
                bulletClassName="text-gray-400 mt-1"
                textClassName="text-gray-600"
              />
            </ResumeSection>
          )}

          {/* Work Experience */}
          {currentData.experiences.length > 0 && (
            <ResumeSection 
              variant="underlined"
              title={titles.experience}
              sectionClassName="mb-10"
            >
              <ExperienceSection 
                experiences={currentData.experiences}
                hasRealData={hasRealData}
                datePosition="left"
                dateClassName="text-sm font-bold text-gray-700"
                companyClassName="text-sm text-gray-600 mb-1"
                positionClassName="text-sm font-bold text-gray-800 mb-2"
                descriptionClassName="text-sm text-gray-600"
                projectTitleClassName="text-sm font-semibold text-gray-700 mb-1"
                projectDescriptionClassName="text-sm text-gray-600"
                bulletStyle="•"
              />
            </ResumeSection>
          )}
        </div>

        {/* Vertical Divider */}
        <div className="w-px bg-gray-300"></div>

        {/* Right Column */}
        <div className="w-1/3 p-8 pl-6">

          {/* Education */}
          {currentData.education.length > 0 && (
            <ResumeSection 
              variant="underlined"
              title={titles.education}
              sectionClassName="mb-10"
            >
              {currentData.education.map((edu, index) => (
                <div key={index} className="mb-4 last:mb-0">
                  <div className={getPlaceholderClass(hasRealData.education, 'text-sm font-bold text-gray-700')}>
                    {edu.startDate} - {edu.endDate}
                  </div>
                  <div className={getPlaceholderClass(hasRealData.education, 'text-sm font-bold text-gray-800 uppercase')}>
                    {edu.school}
                  </div>
                  <div className={getPlaceholderClass(hasRealData.education, 'text-sm text-gray-600')}>
                    {edu.degree}{edu.major ? `, ${edu.major}` : ''}
                  </div>
                  {edu.gpa && (
                    <div className={getPlaceholderClass(hasRealData.education, 'text-sm text-gray-600')}>
                      {edu.gpa}
                    </div>
                  )}
                </div>
              ))}
            </ResumeSection>
          )}

          {/* Certificates */}
          {currentData.certificates.length > 0 && (
            <ResumeSection 
              variant="underlined"
              title={titles.certificates}
              sectionClassName="mb-10"
            >
              <CertificateSection 
                certificates={currentData.certificates}
                hasRealData={hasRealData}
                itemClassName="mb-2"
                dateFormat="inline"
                nameClassName="text-sm font-semibold text-gray-700"
                issuerClassName="text-xs text-gray-600"
              />
            </ResumeSection>
          )}

          {/* Papers */}
          {hasRealData.papers && (
            <ResumeSection 
              variant="underlined"
              title={titles.papers}
              sectionClassName="mb-10"
            >
              <PaperSection 
                papers={currentData.papers}
                hasRealData={hasRealData}
                itemClassName="mb-3"
                titleClassName="text-sm font-semibold text-gray-800"
                authorsClassName="text-xs text-gray-600"
                journalClassName="text-xs text-gray-500"
                showType={false}
              />
            </ResumeSection>
          )}

          {/* Skills */}
          {currentData.skills.length > 0 && (
            <ResumeSection 
              variant="underlined"
              title={titles.skills}
              sectionClassName="mb-10"
            >
              <SkillSection 
                skills={currentData.skills}
                hasRealData={hasRealData}
                variant="list"
                itemClassName="text-sm"
                bulletStyle="•"
              />
            </ResumeSection>
          )}
        </div>
      </div>
    </div>
  );
};

export default Template5;
